import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, BookOpen, GraduationCap, Gamepad2, HeartHandshake, Infinity as InfinityIcon, ShieldCheck, Sparkles, Wallet } from "lucide-react";
import { Reveal } from "../components/Reveal";

const PAIN_POINTS = [
  { icon: Wallet, title: "Droga edukacja dodatkowa", text: "Korepetycje to dziś 80–150 zł za godzinę. Rok nauki z korepetytorem kosztuje tyle, co wakacje całej rodziny." },
  { icon: HeartHandshake, title: "Rodzice bez czasu", text: "Praca, dom, codzienność. Rodzic chce pomóc dziecku w nauce, ale nie zawsze ma czas i cierpliwość do tłumaczenia po raz dziesiąty." },
  { icon: ShieldCheck, title: "Stres i wstydu ucznia", text: "W klasie uczeń boi się zapytać. W domu nie ma kogo spytać. Luki w wiedzy rosną, a pewność siebie spada." },
];

const VALUES = [
  { icon: InfinityIcon, title: "Kupujesz raz — masz na zawsze", text: "Żadnych abonamentów, subskrypcji ani ukrytych opłat. Aplikacja kupiona raz jest Twoja na zawsze." },
  { icon: Sparkles, title: "Ciągły rozwój w cenie", text: "Comiesięczne aktualizacje rozwijają możliwości aplikacji — bez dopłat, automatycznie." },
  { icon: Wallet, title: "Najniższe ceny na rynku", text: "Wierzymy, że dobra edukacja nie powinna być luksusem. Dlatego nasze ceny są uczciwe od pierwszego dnia." },
  { icon: HeartHandshake, title: "Wsparcie, na którym możesz polegać", text: "Realni ludzie, realna pomoc. Jesteśmy z Tobą przed zakupem i długo po nim." },
];

const SHOWCASE = [
  { img: "/img/screenshots/tutor-2.jpg", icon: GraduationCap, title: "Wirtualny korepetytor dla Ciebie", text: "Prywatna lekcja 1-na-1 przez wideorozmowę. Nauczyciel rozmawia głosem, pokazuje zadania na wspólnej tablicy i zaczyna od Twojego poziomu.", href: "/produkt/digitutor-ai" },
  { img: "/img/screenshots/tutor-kids-2.jpg", icon: Sparkles, title: "Bajkowy robot dla dziecka", text: "Uroczy robot w kolorowej klasie zamienia naukę w zabawę. Przyjazny test poznaje poziom dziecka i dopasowuje tempo.", href: "/produkt/digitutor-kids" },
  { img: "/img/screenshots/world-2.jpg", icon: Gamepad2, title: "Gry edukacyjne 3D", text: "Wielkie kolorowe wyspy, zagadki i przygody. Dziecko uczy się matematyki, angielskiego i nie tylko — grając.", href: "/produkt/digilib-world-matematyka" },
  { img: "/img/screenshots/learn-1.jpg", icon: BookOpen, title: "Aplikacje do nauki", text: "Języki obce, czytanie, matematyka dla maluchów — z fiszkami, powtórkami, odznakami i mówionymi lekcjami.", href: "/produkt/digilingua-hiszpanski" },
];

export default function AboutPage() {
  return (
    <div className="relative" data-testid="about-page">
      {/* HERO */}
      <section className="relative mx-auto max-w-5xl px-4 pb-10 pt-20 text-center sm:px-6">
        <Reveal>
          <span className="rounded-full border border-cyan-400/30 bg-cyan-400/10 px-4 py-1.5 text-[11px] font-bold uppercase tracking-widest text-cyan-300">
            O nas — Nasza misja
          </span>
          <h1 className="mt-6 font-display text-4xl font-extrabold leading-tight text-white sm:text-5xl lg:text-6xl">
            Edukacja nie powinna<br />być <span className="gradient-text">luksusem</span>
          </h1>
          <p className="mx-auto mt-6 max-w-2xl text-base leading-relaxed text-slate-300 sm:text-lg">
            DigiLib.store to znacznie więcej niż sklep z ebookami i audiobookami. To centrum edukacyjne,
            które odpowiada na prawdziwe problemy rodziców i uczniów — i pokazuje, że świetna nauka
            może być dostępna dla każdego.
          </p>
        </Reveal>
      </section>

      {/* FILM */}
      <section className="relative mx-auto max-w-5xl px-4 sm:px-6">
        <Reveal>
          <div className="group relative overflow-hidden rounded-[2rem] border border-cyan-400/25 shadow-[0_40px_120px_-30px_rgba(34,211,238,0.4)]" data-testid="mission-video-wrap">
            <video
              data-testid="mission-video"
              className="aspect-video w-full bg-slate-950 object-cover"
              controls
              preload="metadata"
              poster="/video/o-nas-poster.jpg"
              playsInline
            >
              <source src="/video/o-nas-misja.mp4" type="video/mp4" />
              Twoja przeglądarka nie obsługuje wideo.
            </video>
          </div>
          <p className="mt-4 text-center text-sm text-slate-400">
            Poznaj naszą historię w 60 sekund — włącz dźwięk 🎧
          </p>
          <p className="mt-1 text-center text-[11px] text-slate-600">
            Muzyka: „On My Way" — Kevin MacLeod (incompetech.com), licencja CC BY 3.0
          </p>
        </Reveal>
      </section>

      {/* PROBLEMY */}
      <section className="mx-auto max-w-7xl px-4 py-20 sm:px-6">
        <Reveal>
          <h2 className="text-center font-display text-3xl font-extrabold text-white sm:text-4xl">
            Znamy problemy dzisiejszej edukacji
          </h2>
          <p className="mx-auto mt-4 max-w-2xl text-center text-sm text-slate-400 sm:text-base">
            Rozmawialiśmy z setkami rodziców i uczniów. Te same historie wracają jak bumerang.
          </p>
        </Reveal>
        <div className="mt-12 grid gap-6 md:grid-cols-3">
          {PAIN_POINTS.map((p, i) => (
            <Reveal key={p.title} delay={i * 0.1}>
              <div className="glass-panel h-full rounded-3xl p-7 transition-transform duration-300 hover:-translate-y-1.5">
                <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-gradient-to-br from-rose-500/25 to-orange-500/25">
                  <p.icon className="h-6 w-6 text-rose-300" />
                </div>
                <h3 className="mt-5 font-display text-lg font-bold text-white">{p.title}</h3>
                <p className="mt-2 text-sm leading-relaxed text-slate-400">{p.text}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* ODPOWIEDŹ / WARTOŚCI */}
      <section className="relative border-y border-cyan-500/10 bg-slate-950/60 py-20">
        <div className="mx-auto max-w-7xl px-4 sm:px-6">
          <Reveal>
            <h2 className="text-center font-display text-3xl font-extrabold text-white sm:text-4xl">
              Nasza odpowiedź: <span className="gradient-text">edukacja na własność, nie na abonament</span>
            </h2>
          </Reveal>
          <div className="mt-12 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
            {VALUES.map((v, i) => (
              <Reveal key={v.title} delay={i * 0.08}>
                <div className="h-full rounded-3xl border border-cyan-500/15 bg-gradient-to-b from-cyan-500/10 to-transparent p-6 transition-transform duration-300 hover:-translate-y-1.5">
                  <v.icon className="h-7 w-7 text-cyan-300" />
                  <h3 className="mt-4 font-display text-base font-bold text-white">{v.title}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-slate-400">{v.text}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* PRODUKTY */}
      <section className="mx-auto max-w-7xl px-4 py-20 sm:px-6">
        <Reveal>
          <h2 className="text-center font-display text-3xl font-extrabold text-white sm:text-4xl">
            Co tworzymy dla Ciebie i Twojego dziecka
          </h2>
          <p className="mx-auto mt-4 max-w-2xl text-center text-sm text-slate-400 sm:text-base">
            Aplikacje edukacyjne, gry i wirtualni korepetytorzy — wszystko w modelu „kup raz, używaj na zawsze".
          </p>
        </Reveal>
        <div className="mt-12 grid gap-8 md:grid-cols-2">
          {SHOWCASE.map((s, i) => (
            <Reveal key={s.title} delay={i * 0.08}>
              <Link to={s.href} className="group block overflow-hidden rounded-3xl border border-slate-700/60 bg-slate-900/50 transition-all duration-300 hover:-translate-y-1.5 hover:border-cyan-400/40 hover:shadow-[0_25px_70px_-25px_rgba(34,211,238,0.4)]" data-testid={`about-showcase-${i}`}>
                <div className="overflow-hidden">
                  <img src={s.img} alt={s.title} loading="lazy" className="aspect-video w-full object-cover transition-transform duration-700 group-hover:scale-105" />
                </div>
                <div className="p-6">
                  <div className="flex items-center gap-2.5">
                    <s.icon className="h-5 w-5 text-cyan-300" />
                    <h3 className="font-display text-lg font-bold text-white">{s.title}</h3>
                  </div>
                  <p className="mt-2 text-sm leading-relaxed text-slate-400">{s.text}</p>
                </div>
              </Link>
            </Reveal>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section className="mx-auto max-w-4xl px-4 pb-24 text-center sm:px-6">
        <Reveal>
          <h2 className="font-display text-3xl font-extrabold text-white sm:text-4xl">
            Dołącz do rodzin, które uczą się <span className="gradient-text">mądrzej</span>
          </h2>
          <p className="mx-auto mt-4 max-w-xl text-sm text-slate-400 sm:text-base">
            Ebooki, audiobooki, aplikacje, gry i wirtualni korepetytorzy. Bez abonamentów. Bez subskrypcji. Bez wysokich kosztów.
          </p>
          <Link
            to="/"
            data-testid="about-cta-btn"
            className="group mt-8 inline-flex items-center gap-2 rounded-full bg-gradient-to-r from-cyan-500 to-teal-400 px-8 py-4 text-sm font-bold text-slate-950 shadow-lg shadow-cyan-500/30 transition-transform hover:scale-105 active:scale-95"
          >
            Odkryj DigiLib.store
            <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
          </Link>
        </Reveal>
      </section>
    </div>
  );
}
